// Behavior registry for the DB-driven global toolbar (global_menu_buttons.action_code).
// Structure (which cards/buttons exist, their label/icon/order/control type)
// now lives in the database — see global_menu_renderer.js — but the actual
// logic behind each action stays code, exactly like system.toolbarButtons'
// `action` already works for the per-system toolbar. This is a straight
// move of what used to be inline in app_event_controller.js#bindAppEvents():
// same functions, same S.project fields, same recordUndo()/refresh() calls
// — only the binding site changed, from one addEventListener per hard-coded
// element id to a single delegated listener reading `data-action-code`.
//
// Each entry has a `kind` ('button' | 'checkbox' | 'dropdown' | 'input') and:
// - button:            { onClick(element) }
// - checkbox/dropdown/input: { getValue(), onChange(value) }
// `getValue()` always reads live S.project state — a global_menu_button's
// `default_value` in the DB is only the render-time fallback for a
// project that doesn't have that field yet, never the source of truth.
import * as AppController from './app_controller.js?v=20260904f';
import * as AppOptionsController from './app_options_controller.js?v=20260904f';
import { S } from './app_state.js?v=20260817c';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import {
  recordUndo,
  undoLast,
  redoLast
} from '../services/history_service.js?v=20260903c';
import { ProjectPersistenceService } from '../project/project_persistence_service.js?v=20260904f';
import { clearProjectFloorplan } from '../project/project_state_service.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import * as ClipboardController from '../selection/clipboard_controller.js?v=20260904f';
import * as FloorplanController from '../viewport/floorplan_controller.js?v=20260903c';
import { handleToolAction } from '../interaction/tool_service.js?v=20260904f';
import { toggleSketchWallDrawTool } from '../sketch_walls/sketch_wall_interaction.js?v=20260903c';
import { showDrawingFeedback } from '../ui/drawing_feedback.js?v=20260717a';
import { withWorkspaceLoadingOverlay } from '../ui/workspace_loading_overlay.js?v=20260826a';
import {
  DEFAULT_PROJECT_LEVELS,
  normalizeProjectLevels
} from '../utils/common_constants.js?v=20260706d';
import {
  invokeView3DAction,
  openThreePdfExportModal
} from './three_lazy_runtime.js?v=20260904f';
import {
  openBomModal,
  openSaveAsOrderModal,
  openPrintPlanModal,
  openInformationModal
} from './lazy_dialog_runtime.js?v=20260904f';

function runLazy3DAction(method, ...args) {
  return invokeView3DAction(method, ...args).catch(error => {
    console.error(`3D-Aktion fehlgeschlagen: ${method}`, error);
    setStatus(`3D-Funktion konnte nicht geladen werden: ${error?.message || error}`);
    return null;
  });
}

function openLazy3DPdfExportModal() {
  return openThreePdfExportModal().catch(error => {
    console.error('3D-PDF-Dialog konnte nicht geladen werden', error);
    setStatus(`3D-PDF konnte nicht geladen werden: ${error?.message || error}`);
    return null;
  });
}

function runLazyDialogAction(action, label) {
  return action().catch(error => {
    console.error(`${label} konnte nicht geladen werden`, error);
    setStatus(`${label} konnte nicht geladen werden: ${error?.message || error}`);
    return null;
  });
}

let manualSaveInFlight = false;

const GLOBAL_MENU_ACTIONS = {
  'undo': {
    kind: 'button',
    onClick: () => { if (undoLast()) AppController.refresh(false); }
  },
  'redo': {
    kind: 'button',
    onClick: () => { if (redoLast()) AppController.refresh(false); }
  },
  'context-copy': {
    kind: 'button',
    onClick: () => ClipboardController.copyCurrentSelectionToClipboard()
  },
  'context-paste': {
    kind: 'button',
    onClick: () => ClipboardController.pasteFromAppClipboard()
  },
  'new-project': {
    kind: 'button',
    onClick: () => {
      ProjectPersistenceService.startNewDetachedProject().catch(error => {
        console.error('Neuer Auftrag konnte nicht geöffnet werden', error);
        setStatus(`Neuer Auftrag konnte nicht geöffnet werden: ${error.message || error}`);
      });
    }
  },
  'save-project': {
    kind: 'button',
    onClick: async () => {
      if (manualSaveInFlight) return;
      manualSaveInFlight = true;
      let saved = null;
      try {
        saved = await withWorkspaceLoadingOverlay(
          () => ProjectPersistenceService.manualSaveCurrentState(),
          {
            label: 'Auftrag wird gespeichert...',
            details: 'Bitte warten, bis der Speichervorgang abgeschlossen ist.',
            allowDialogs: true
          }
        );
      } catch (error) {
        console.error('Auftrag konnte nicht gespeichert werden', error);
      } finally {
        manualSaveInFlight = false;
      }
      showDrawingFeedback(
        saved ? 'Erfolgreich gespeichert' : 'Fehler beim Speichern',
        saved ? 'success' : 'error'
      );
    }
  },
  'save-as-project': {
    kind: 'button',
    onClick: () => runLazyDialogAction(openSaveAsOrderModal, 'Speichern-unter-Dialog')
  },
  'open-print-modal': {
    kind: 'button',
    onClick: () => runLazyDialogAction(openPrintPlanModal, 'Druckvorschau')
  },
  'load-floorplan-file': {
    kind: 'file',
    onChange: file => FloorplanController.importFloorplanFile(file)
  },
  'load-cad-floorplan-file': {
    kind: 'file',
    onChange: file => FloorplanController.importCadFloorplanFile(file)
  },
  'satellite-floorplan': {
    kind: 'button',
    onClick: () => FloorplanController.openSatelliteMapImport()
  },
  'grid-size': {
    kind: 'dropdown',
    getValue: () => String(S.project.gridSize ?? 10),
    onChange: value => {
      recordUndo('Raster ändern');
      AppOptionsController.markProjectGridOptionsTouched?.();
      S.project.gridSize = Number(value);
      AppOptionsController.persistAppOptions();
      AppController.refresh(false);
    }
  },
  'helper-grid-size': {
    kind: 'dropdown',
    getValue: () => String(S.project.helperGridSize ?? 600),
    onChange: value => {
      recordUndo('Hilfsraster ändern');
      AppOptionsController.markProjectGridOptionsTouched?.();
      S.project.helperGridSize = Number(value);
      AppOptionsController.persistAppOptions();
      AppController.refresh(false);
    }
  },
  'snap-toggle': {
    kind: 'checkbox',
    getValue: () => S.project.snap !== false,
    onChange: checked => {
      recordUndo('Snap ändern');
      AppOptionsController.markProjectGridOptionsTouched?.();
      S.project.snap = checked;
      AppOptionsController.persistAppOptions();
      AppController.refresh(false);
    }
  },
  'grid-toggle': {
    kind: 'checkbox',
    getValue: () => S.project.showGrid !== false,
    onChange: checked => {
      recordUndo('Raster anzeigen');
      AppOptionsController.markProjectGridOptionsTouched?.();
      S.project.showGrid = checked;
      AppOptionsController.persistAppOptions();
      // Werkzeugleiste wird nur einmal beim Start gerendert (nicht bei
      // jedem refresh()) -- selGridSize/chkSnap haengen von chkGrid ab,
      // deshalb hier direkt live nachziehen statt auf ein Re-Render zu
      // warten, siehe gridActionsHiddenAttr() in global_menu_renderer.js.
      document.getElementById('selGridSize')?.toggleAttribute('hidden', !checked);
      document.getElementById('chkSnap')?.closest('label')?.toggleAttribute('hidden', !checked);
      AppController.refresh(false);
    }
  },
  'helper-grid-toggle': {
    kind: 'checkbox',
    getValue: () => S.project.showHelperGrid === true,
    onChange: checked => {
      recordUndo('Hilfsraster anzeigen');
      AppOptionsController.markProjectGridOptionsTouched?.();
      S.project.showHelperGrid = checked;
      AppOptionsController.persistAppOptions();
      document.getElementById('helperGridSize')?.toggleAttribute('hidden', !checked);
      AppController.refresh(false);
    }
  },
  'room-height': {
    kind: 'input',
    getValue: () => String(Math.round(normalizeProjectLevels(S.project.levels).rh)),
    onChange: value => {
      const numericValue = Number(value);
      const nextValue = Math.max(0, Number.isFinite(numericValue) ? numericValue : DEFAULT_PROJECT_LEVELS.rh);
      recordUndo('Raumhöhe ändern');
      S.project.levels = normalizeProjectLevels(S.project.levels);
      const previousRh = S.project.levels.rh;
      const levelChange = {
        field: 'rh',
        previousValue: previousRh,
        nextValue,
        previousLevels: { ...S.project.levels }
      };
      SystemRegistry.beforeProjectLevelsChange(levelChange);
      S.project.levels.rh = nextValue;
      S.project.levels = normalizeProjectLevels(S.project.levels);
      levelChange.nextLevels = { ...S.project.levels };
      SystemRegistry.afterProjectLevelsChange(levelChange);
      AppController.refresh(false);
    }
  },
  'clear-floorplan': {
    kind: 'button',
    isVisible: () => Boolean(S.project?.floorplan),
    onClick: () => {
      if (!S.project?.floorplan) return false;
      clearProjectFloorplan();
      AppController.refresh(false);
      return true;
    }
  },
  'toggle-levels': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'levels')
  },
  'dimension-tool': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'dimension')
  },
  'angular-dimension-tool': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'angular_dimension')
  },
  'area-measurement-tool': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'area_measurement')
  },
  'auto-area-measurement-tool': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'auto_area_measurement')
  },
  'text-annotation-tool': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'text_annotation')
  },
  'bom': {
    kind: 'button',
    onClick: () => runLazyDialogAction(openBomModal, 'Stückliste')
  },
  'open-information-modal': {
    kind: 'button',
    onClick: () => runLazyDialogAction(openInformationModal, 'Informationen')
  },
  'dxf-export': {
    kind: 'button',
    onClick: () => runLazy3DAction('exportProjectDxf')
  },
  'glb-export': {
    kind: 'button',
    onClick: element => runLazy3DAction('exportProjectGlb', element)
  },
  '3dpdf-export': {
    kind: 'button',
    onClick: openLazy3DPdfExportModal
  },
  'wireframe-export': {
    kind: 'button',
    onClick: () => runLazy3DAction('exportProjectWireframe')
  },
  'step-export': {
    kind: 'button',
    onClick: () => runLazy3DAction('exportProjectStep')
  },
  '3d-view': {
    kind: 'button',
    onClick: () => runLazy3DAction('open3DModal')
  },
  'obstacle-pipe': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'obstacle_pipe')
  },
  'obstacle-duct': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'obstacle_duct')
  },
  'sketch-wall': {
    kind: 'button',
    onClick: () => toggleSketchWallDrawTool()
  },
  'object-library': {
    kind: 'button',
    onClick: () => handleToolAction('core', 'object_library')
  }
};

export { GLOBAL_MENU_ACTIONS };
